import { z } from 'zod';
import { montarCaminho, requisitar } from './client.js';
import type { Pagina, ParametrosDePagina } from './paginacao.js';
import { paginaSchema, parametrosDePagina } from './paginacao.js';
import { analisar } from './validacao.js';

/**
 * Inventário do painel (RF-062, RF-063, RF-073, RF-082).
 *
 * A listagem é de CICLOS — uma linha por `Inventario` —, e não de contagens: o
 * mesmo depósito tem N contagens ao longo do tempo, e listá-las soltas faria um
 * conflito parecer dois registros independentes. Quem resume a contagem mais
 * recente é o servidor, e o painel **não soma nem elege contagem nenhuma**: a
 * escolha entre contagens concorrentes é decisão humana (RF-063).
 */

const CAMINHO_COLECAO = montarCaminho('/inventarios');

/** Estado cru da máquina da spec §6.3 — a tela pode ser precisa quando quiser. */
export const statusDoInventarioSchema = z.enum([
  'AbertoNoErp',
  'EmAndamento',
  'AguardandoValidacao',
  'Enviado',
  'EmConflito',
  'ComDivergencia',
  'Resolvido',
  'Finalizado',
]);

/**
 * A derivação que o SERVIDOR faz do status e das contagens, e que a tela usa no
 * chip. Vem pronta de propósito: derivá-la aqui faria o chip da lista discordar
 * do chip do detalhe no dia em que a regra mudasse.
 */
export const situacaoDoInventarioSchema = z.enum([
  'emContagem',
  'conflito',
  'aguardandoRevisao',
  'resolvido',
]);

export type StatusDoInventario = z.infer<typeof statusDoInventarioSchema>;
export type SituacaoDoInventario = z.infer<typeof situacaoDoInventarioSchema>;

export const operadorDaContagemSchema = z.object({
  id: z.string(),
  matricula: z.string(),
  nome: z.string(),
});

export type OperadorDaContagem = z.infer<typeof operadorDaContagemSchema>;

export const inventarioDaListaSchema = z.object({
  /** Id do CICLO — é ele que vai na rota de detalhe, e não o depósito. */
  id: z.string(),
  /** Com `depositoErp`, o "Local" da tela. Nulo só em ciclo antigo, de antes da filial. */
  codFil: z.number().int().nullable(),
  depositoErp: z.string(),
  status: statusDoInventarioSchema,
  situacao: situacaoDoInventarioSchema,
  /** RF-082 — enquanto true, o depósito recusa coleta e movimentação. */
  bloqueante: z.boolean(),
  numeroErp: z.string().nullable(),
  /** Relógio do SERVIDOR: é por ele que o período filtra e a lista ordena. */
  abertoEm: z.string(),
  finalizadoEm: z.string().nullable(),
  /** Relógio do APARELHO da contagem mais recente (RF-052). */
  ultimaContagemEm: z.string().nullable(),
  /** Relógio do SERVIDOR da MESMA contagem — a distância é o tempo offline. */
  ultimaContagemRecebidaEm: z.string().nullable(),
  /** Mais de uma significa conflito (RF-062). */
  totalContagens: z.number().int().nonnegative(),
  /** Nulo, e não zero, enquanto ninguém contou: são fatos diferentes. */
  totalSkusUltimaContagem: z.number().int().nonnegative().nullable(),
  /** Texto decimal: `Decimal(18,6)` não sobrevive ao ponto flutuante. */
  totalUnidadesUltimaContagem: z.string().nullable(),
  divergenciasEmAberto: z.number().int().nonnegative(),
  divergenciasTotal: z.number().int().nonnegative(),
  operador: operadorDaContagemSchema.nullable(),
});

export type InventarioDaLista = z.infer<typeof inventarioDaListaSchema>;

const paginaDeInventariosSchema = paginaSchema(inventarioDaListaSchema);

export type FiltroDeInventarios = ParametrosDePagina & {
  /** Recorta por `abertoEm` (ISO), o mesmo campo por onde a lista ordena. */
  readonly de?: string;
  readonly ate?: string;
  /** Igualdade EXATA (caixa não importa): é código do ERP, não campo de busca. */
  readonly deposito?: string;
  readonly codFil?: number;
  readonly status?: StatusDoInventario;
  readonly emConflito?: boolean;
  readonly comDivergenciaEmAberto?: boolean;
  readonly bloqueante?: boolean;
  /** Traz o ciclo em que QUALQUER contagem foi daquela pessoa. */
  readonly operadorId?: string;
};

export async function listarInventarios(
  filtro: FiltroDeInventarios = {},
): Promise<Pagina<InventarioDaLista>> {
  const corpo = await requisitar(CAMINHO_COLECAO, {
    consulta: {
      ...parametrosDePagina(filtro),
      de: filtro.de,
      ate: filtro.ate,
      deposito: filtro.deposito,
      codFil: filtro.codFil,
      status: filtro.status,
      emConflito: filtro.emConflito,
      comDivergenciaEmAberto: filtro.comDivergenciaEmAberto,
      bloqueante: filtro.bloqueante,
      operadorId: filtro.operadorId,
    },
  });

  return analisar(paginaDeInventariosSchema, corpo, CAMINHO_COLECAO);
}

export const statusDaOperacaoSchema = z.enum([
  'PendenteDispositivo',
  'Registrada',
  'Retida',
  'Enviando',
  'Consolidada',
  'Rejeitada',
  'Cancelada',
]);

export const aparelhoDaContagemSchema = z.object({
  id: z.string(),
  identificador: z.string(),
  apelido: z.string().nullable(),
});

export const itemDaContagemSchema = z.object({
  id: z.string(),
  /** CÓDIGO do produto. Não há cadastro de descrição no schema do middleware. */
  produto: z.string(),
  derivacao: z.string(),
  quantidade: z.string(),
  /** Ordem em que o operador registrou e conferiu (UC07 6). */
  ordem: z.number().int(),
});

export type ItemDaContagem = z.infer<typeof itemDaContagemSchema>;

export const contagemDoCicloSchema = z.object({
  id: z.string(),
  operacaoId: z.string(),
  sequencia: z.number().int(),
  statusDaOperacao: statusDaOperacaoSchema,
  /** RF-062 — marcado nas DUAS contagens do mesmo local, nunca em uma só. */
  emConflito: z.boolean(),
  totalSkus: z.number().int().nonnegative(),
  totalUnidades: z.string(),
  observacao: z.string().nullable(),
  /** Relógio do APARELHO: quando o operador confirmou o resumo (RF-052). */
  registradoEm: z.string(),
  /** Relógio do SERVIDOR: quando o middleware gravou. */
  recebidaEm: z.string(),
  operador: operadorDaContagemSchema.nullable(),
  dispositivo: aparelhoDaContagemSchema.nullable(),
  itens: z.array(itemDaContagemSchema),
});

export type ContagemDoCiclo = z.infer<typeof contagemDoCicloSchema>;

export const resolucaoDaDivergenciaSchema = z.enum([
  'Pendente',
  'AceitarContagem',
  'ManterErp',
  'Recontar',
]);

export const divergenciaSchema = z.object({
  id: z.string(),
  produto: z.string(),
  derivacao: z.string().nullable(),
  saldoErp: z.string(),
  contado: z.string(),
  /** Texto decimal COM SINAL: `-2.000000` é falta física. */
  diferenca: z.string(),
  /** RF-063 — só muda por decisão humana. */
  resolucao: resolucaoDaDivergenciaSchema,
  resolvidoPor: z.string().nullable(),
  resolvidoEm: z.string().nullable(),
  criadoEm: z.string(),
});

export type Divergencia = z.infer<typeof divergenciaSchema>;

export const linhaDivergenteEntreContagensSchema = z.object({
  produto: z.string(),
  derivacao: z.string(),
  /** Uma entrada por contagem, na MESMA ordem de `contagens`. */
  quantidades: z.array(
    z.object({
      contagemId: z.string(),
      /** Nulo = a chave não existe naquela contagem (item que só um viu). */
      quantidade: z.string().nullable(),
    }),
  ),
});

export type LinhaDivergenteEntreContagens = z.infer<typeof linhaDivergenteEntreContagensSchema>;

export const totaisDoInventarioSchema = z.object({
  contagens: z.number().int().nonnegative(),
  skusUltimaContagem: z.number().int().nonnegative().nullable(),
  unidadesUltimaContagem: z.string().nullable(),
  divergenciasEmAberto: z.number().int().nonnegative(),
  divergenciasTotal: z.number().int().nonnegative(),
  /** Divergências com diferença negativa — falta física. */
  faltas: z.number().int().nonnegative(),
  sobras: z.number().int().nonnegative(),
  /** Soma das diferenças, com sinal. `"0"` sem divergências. */
  saldoLiquido: z.string(),
});

export const detalheInventarioSchema = z.object({
  id: z.string(),
  codFil: z.number().int().nullable(),
  depositoErp: z.string(),
  status: statusDoInventarioSchema,
  situacao: situacaoDoInventarioSchema,
  bloqueante: z.boolean(),
  numeroErp: z.string().nullable(),
  abertoEm: z.string(),
  finalizadoEm: z.string().nullable(),
  totais: totaisDoInventarioSchema,
  /** TODAS as contagens do ciclo, em ordem cronológica do aparelho. */
  contagens: z.array(contagemDoCicloSchema),
  /** Comparação com o ERP (RF-073) — vazia até o conector existir. */
  divergencias: z.array(divergenciaSchema),
  /** O diff da tela de conflito, calculado no servidor. */
  divergenciasEntreContagens: z.array(linhaDivergenteEntreContagensSchema),
});

export type DetalheInventario = z.infer<typeof detalheInventarioSchema>;

/**
 * Um ciclo inteiro: contagens, divergências e o diff entre elas.
 *
 * Serve as DUAS telas — detalhe e conflito. Não há consulta própria de
 * conflito porque ela precisaria de uma regra de "quais duas contagens", e
 * escolher já é uma forma de resolver o conflito (RF-063).
 */
export async function consultarInventario(id: string): Promise<DetalheInventario> {
  const caminho = montarCaminho('/inventarios/{id}', { id });

  return analisar(detalheInventarioSchema, await requisitar(caminho), caminho);
}

export type TipoDaDiferenca = 'falta' | 'sobra' | 'conferido';

/**
 * Falta, sobra ou conferido — lido do SINAL do texto decimal.
 *
 * É a única derivação que sobra para o painel na comparação com o ERP, e ela
 * olha o número, não o texto: `-0.000000` continua sendo conferido, e comparar
 * com a string `'0'` chamaria de falta uma linha que fecha.
 */
export function tipoDaDiferenca(diferenca: string): TipoDaDiferenca {
  const valor = Number(diferenca);

  if (!Number.isFinite(valor) || valor === 0) {
    return 'conferido';
  }

  return valor < 0 ? 'falta' : 'sobra';
}

/** Identidade de uma linha contada: o código sozinho não distingue derivações. */
export function chaveDoProduto(produto: string, derivacao: string): string {
  return `${produto}|${derivacao}`;
}

/**
 * As chaves que o SERVIDOR apontou como divergentes entre contagens.
 *
 * A tela destaca por este conjunto em vez de recalcular o diff: a comparação
 * precisa valer para N contagens e conhecer a igualdade decimal (`3.5` e
 * `3.500000` são o mesmo fato), e as duas coisas moram no middleware.
 */
export function chavesDivergentes(detalhe: DetalheInventario): ReadonlySet<string> {
  return new Set(
    detalhe.divergenciasEntreContagens.map((linha) =>
      chaveDoProduto(linha.produto, linha.derivacao),
    ),
  );
}
